import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { sortTopmostFirst } from '../helpers/paint-order'
import { isShapeSelectable } from '../helpers/selection-scope'
import type { LocalPoint } from '../types/interaction-state'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { EditScopeRefsApi } from './use-edit-scope-refs'

export function useShapeHitTesting(ctx: ShapeManagerStateApi & EditScopeRefsApi) {
  const { containerRef, ctrlCycleRef, editScopeGroupIdRef, elementZIndexMap, scaleRef, shapesRef } =
    ctx

  const getLocalPointFromEvent = useCallback(
    (event: ReactPointerEvent): LocalPoint | null => {
      const container = containerRef.current

      if (!container) return null
      const rect = container.getBoundingClientRect()
      const currentScale = scaleRef.current || 1

      return {
        x: (event.clientX - rect.left) / currentScale,
        y: (event.clientY - rect.top) / currentScale,
      }
    },
    [containerRef, scaleRef]
  )

  const getLocalPointFromClient = useCallback(
    (clientX: number, clientY: number): LocalPoint | null => {
      const container = containerRef.current

      if (!container) return null
      const rect = container.getBoundingClientRect()
      const currentScale = scaleRef.current || 1

      return {
        x: (clientX - rect.left) / currentScale,
        y: (clientY - rect.top) / currentScale,
      }
    },
    [containerRef, scaleRef]
  )

  // Topmost first, as the canvas paints them (`sortTopmostFirst`), not in list order.
  const getStackAtPoint = useCallback(
    (point: LocalPoint): ShapeData[] => {
      const hits = shapesRef.current.filter((s) => {
        if (s.hidden === true || !isShapeSelectable(s, editScopeGroupIdRef.current)) return false
        const w = s.width ?? 0
        const h = s.height ?? 0

        return point.x >= s.x && point.x <= s.x + w && point.y >= s.y && point.y <= s.y + h
      })

      return sortTopmostFirst(hits, shapesRef.current, elementZIndexMap)
    },
    [editScopeGroupIdRef, elementZIndexMap, shapesRef]
  )

  const getCtrlCycleTargetAtPoint = useCallback(
    (point: LocalPoint): ShapeData | null => {
      const stack = getStackAtPoint(point)

      if (stack.length === 0) {
        ctrlCycleRef.current = null
        return null
      }

      // Round to whole pixels so tiny pointer jitter does not break cycle continuity.
      const key = `${Math.round(point.x)}:${Math.round(point.y)}`
      const stackSignature = stack.map((shape) => shape.id).join('|')
      const currentCycle = ctrlCycleRef.current
      const isSameCycle =
        currentCycle !== null &&
        currentCycle.key === key &&
        currentCycle.stackSignature === stackSignature
      const targetIndex = isSameCycle ? currentCycle.nextIndex % stack.length : 0
      const nextIndex = (targetIndex + 1) % stack.length

      ctrlCycleRef.current = { key, stackSignature, nextIndex }
      return stack[targetIndex] ?? null
    },
    [getStackAtPoint, ctrlCycleRef]
  )

  return {
    getLocalPointFromEvent,
    getLocalPointFromClient,
    getStackAtPoint,
    getCtrlCycleTargetAtPoint,
  }
}

export type ShapeHitTestingApi = ReturnType<typeof useShapeHitTesting>
